import React, { useState } from "react";
import {
  alpha,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Drawer,
  Collapse,
  Switch,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchBar from "components/SearchBar/SearchBar";
import { filters as FILTER_CONFIG } from "utils/cassandraFields";
import default_theme from "assets/themes/themes";

const IOSwitch = styled(Switch)(({ theme }) => ({
  width: 44,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(18px)",
      color: {backgroundColor: default_theme.palette.other.main},
      "& + .MuiSwitch-track": {
        backgroundColor: default_theme.palette.other.main,
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": { width: 22, height: 22, boxShadow: "none" },
  "& .MuiSwitch-track": {
    borderRadius: 26,
    opacity: 1,
    backgroundColor: default_theme.palette.grey[400],
  },
}));

const FilterSidebar = ({
  filtersOpen,
  setFiltersOpen,
  selectedFilters,
  handleFilterChange,
  clearAllFilters,
  getActiveFiltersCount,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const theme = useTheme();
  const activeCount = getActiveFiltersCount();
  
  const [locationSearchQuery, setLocationSearchQuery] = useState("");

  const getFilteredLocationOptions = () => {
    const locationCategory = FILTER_CONFIG["Location(Country)"] || [];
    
    if (!locationCategory || locationCategory.length === 0) return [];
    
    if (!locationSearchQuery.trim()) {
      return locationCategory;
    }
    
    return locationCategory.filter(location =>
      location.toLowerCase().includes(locationSearchQuery.toLowerCase())
    );
  };

  const handleLocationSearchChange = (event) => {
    setLocationSearchQuery(event.target.value);
  };

  const FilterPanel = (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "background.paper",
        borderColor: "divider",
      }}
    >
      <Grid container spacing={3}>
        {Object.keys(FILTER_CONFIG).map((category) => {
          const isLocationCategory = category === "Location(Country)";
          
          return (
            <Grid item xs={12} md={4} key={category}>
              {isLocationCategory ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'baseline', 
                  gap: 1.5, 
                  flexWrap: 'wrap',
                  justifyContent: 'space-between'
                }}>
                  <Typography sx={{ 
                    fontWeight: 700, 
                    minWidth: 'fit-content',
                    whiteSpace: 'nowrap'
                  }}>
                    {category}
                  </Typography>
                  <Box sx={{ 
                    flex: '0 1 150px', 
                    minWidth: '120px', 
                    '& .MuiTextField-root': {
                      '& .MuiInputBase-root': {
                        height: '25px', 
                        fontSize: '0.875rem' 
                      }
                    }
                  }}>
                    <SearchBar
                      searchQuery={locationSearchQuery}
                      onSearchChange={handleLocationSearchChange}
                    />
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ fontWeight: 700, mb: 1 }}>{category}</Typography>
              )}
              
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  maxHeight: 180,
                  overflowY: "auto",
                  pr: 1,
                }}
              >
                {(isLocationCategory ? getFilteredLocationOptions() : FILTER_CONFIG[category]).map((value) => {
                  const checked = !!selectedFilters[category]?.includes(value);
                  return (
                    <Box key={value} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <IOSwitch
                        checked={checked}
                        onChange={() => handleFilterChange(category, value)}
                      />
                      <Typography sx={{ lineHeight: 1.2 }}>{value}</Typography>
                    </Box>
                  );
                })}
                
                {isLocationCategory && getFilteredLocationOptions().length === 0 && locationSearchQuery.trim() && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    No countries found matching "{locationSearchQuery}"
                  </Typography>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <Box sx={{
        mt: 3,
        pt: 2,
        borderTop: "1px solid",
        borderColor: "divider",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <Typography variant="body2" color="text.secondary">
          {activeCount} filter{activeCount === 1 ? "" : "s"} selected
        </Typography>
        <Button
          onClick={() => {
            clearAllFilters();
            setLocationSearchQuery(""); 
          }}
          size="small"
          variant="outlined"
          sx={{
            color: default_theme.palette.other.main,
            borderColor: default_theme.palette.other.main,
            "&:hover": {
              backgroundColor: alpha(default_theme.palette.other.main,'0.4'),
              borderColor: default_theme.palette.other.main,
            },
          }}
        >
          Clear Filters
        </Button>
      </Box>
    </Paper>
  );

  return isMobile ? (
    <Drawer
      anchor="top"
      open={filtersOpen}
      onClose={() => setFiltersOpen(false)}
      PaperProps={{ sx: { p: 2 } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Filters</Typography>
        <IconButton onClick={() => setFiltersOpen(false)}><CloseIcon /></IconButton>
      </Box>
      {FilterPanel}
    </Drawer>
  ) : (
    <Collapse in={filtersOpen} appear>
      {FilterPanel}
    </Collapse>
  );
};

export default FilterSidebar;